import React, { Suspense } from "react";
import { createBrowserRouter, Outlet } from "react-router-dom";
import authV2Routes from "feature/authv2/routes";
import AppLayout from "app/layouts/AppLayout";
import driverRoutes from "feature/driver/routes";
import fleetRoutes from "feature/fleet/routes";
import crmRoutes from "feature/crm/routes";
import driverManagementRoutes from "feature/driver-management/routes";
import tallyRoutes from "feature/tally/routes";
import reportsRoutes from "feature/reports/routes";
import controlTowerRoutes from "feature/control-tower/routes";
import fleetManagementRoutes from "feature/fleet-management/routes";
import settlementRoutes from "feature/settlement/routes";
import userRoutes from "feature/user/routes";
import performanceRoutes from "feature/performance/routes";
import rolesPermissionsRoutes from "feature/roles-permissions/routes";
import GtmPageViewTracker from "app/wrappers/GtmPageViewTracker";
import ClientLogoutHandler from "app/wrappers/ClientLogoutHandler";
import lazyWithRetry from "app/utils/lazyWithRetry";
import ProtectedRoute from "app/boundaries/ProtectedRouteBoundary";

const JobSnackBar = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "job-snack-bar" */
      "services/jobManager/JobSnackBar"
    )
);
import DefaultRoute from "app/routes/DefaultRoute";
import DashboardIndexRoute from "app/routes/DashboardIndexRoute";
import { wrapRoutes } from "../routes/route.utils";
import PageNotFound from "app/pages/PageNotFound";

const isDev = process.env.NODE_ENV !== "production";

const featureModules = [
  driverRoutes,
  fleetRoutes,
  crmRoutes,
  driverManagementRoutes,
  tallyRoutes,
  reportsRoutes,
  controlTowerRoutes,
  fleetManagementRoutes,
  settlementRoutes,
  userRoutes,
  performanceRoutes,
];

const dashboardRoutes = featureModules.flatMap((f) => f?.dashboard ?? []);
const standaloneRoutes = featureModules.flatMap((f) => f?.standalone ?? []);

/**
 * Path prefixes for routes that are not protected and not authorized (public).
 * When logout is triggered (e.g. 401) on these paths, we do not redirect to /login so the user can stay on the page.
 */
export function getPublicPathPrefixes() {
  const standalone = [
    ...standaloneRoutes,
    ...(rolesPermissionsRoutes?.standalone ?? []),
    ...(authV2Routes?.standalone ?? []),
  ];
  return standalone
    .filter((r) => !r.protected && (!r.authorized || r.authorized.length === 0))
    .map((r) => {
      const p = r.path || "";
      return p.replace(/\/:[^/]*$/, "").replace(/\/$/, "") || p;
    })
    .filter(Boolean);
}

let devOnlyRoutes = [];
if (isDev) {
  const ButtonShowcase = lazyWithRetry(() => import("components/ui/buttons/ButtonShowcase"));
  const IconShowcase = lazyWithRetry(() => import("components/ui/icons/IconShowcase"));
  const OCRTest = lazyWithRetry(() => import("../../feature/test/OCRTest"));
  devOnlyRoutes = [
    {
      path: "/buttons-showcase",
      element: <ButtonShowcase />,
    },
    {
      path: "/icons-showcase",
      element: <IconShowcase />,
    },
    {
      path: "/ocr-test",
      element: <OCRTest />,
      protected: true,
    },
  ];
}
const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <>
        <ClientLogoutHandler />
        <GtmPageViewTracker />
        <Suspense fallback={null}>
          <JobSnackBar />
        </Suspense>
        <Outlet />
      </>
    ),
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <DefaultRoute />
          </ProtectedRoute>
        ),
      },
      {
        path: "/dashboard/",
        element: <AppLayout />,
        children: [
          {
            index: true,
            element: <DashboardIndexRoute />,
          },
          ...wrapRoutes(dashboardRoutes),
          {
            path: "*",
            element: (
              <PageNotFound
                style={{ backgroundColor: "rgb(244, 247, 255)" }}
                title={" "}
                subTitle="Sorry, the tab you visited does not exist."
              />
            ),
          },
        ],
      },
      ...wrapRoutes(standaloneRoutes),
      ...wrapRoutes(rolesPermissionsRoutes.standalone),
      ...wrapRoutes(authV2Routes.standalone),
      ...devOnlyRoutes,
      {
        path: "*",
        element: <DefaultRoute />,
      },
    ],
  },
]);
export const routesPermissions = dashboardRoutes?.map((route) => {
  const r = { ...route };
  delete r.element;
  delete r.lazyComponent;
  return r;
});
export default router;
